import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { firstValueFrom } from 'rxjs';

import { CookieName } from '../../browser/cookie/cookie.models';
import { CookieService } from '../../browser/cookie/cookie.service';
import { ClientConfigService } from '../../client-config/client-config.service';
import { Page } from '../../client-config/page.client-config';
import { DatadogRumService } from '../../datadog-rum/datadog-rum.service';
import { GoogleAnalyticsCoreService } from '../../lazy/service-providers/google-analytics-core.service';
import { Logger } from '../../logging/logger';
import { NativeEventType } from '../../native-app/native-app.models';
import { NativeAppService } from '../../native-app/native-app.service';
import { GoToOptions } from '../../navigation/navigation.models';
import { NavigationService } from '../../navigation/navigation.service';
import { ParsedUrl } from '../../navigation/parsed-url';
import { UrlService } from '../../navigation/url.service';
import { TrackingService } from '../../tracking/tracking-core.service';
import { ClaimsConfig } from '../../user/claims.client-config';
import { ClaimTypeFullName } from '../../user/claims.models';
import { UserLoginEvent, UserPartialLoginEvent, UserPreHooksLoginEvent } from '../../user/user-events';
import { UserConfig } from '../../user/user.client-config';
import { UserService } from '../../user/user.service';
import { LoginNavigationService } from '../login-navigation.service';
import { LoginStoreService } from '../login-store.service';
import { LoginRedirectInfo, LoginResponse } from '../login.models';
import { LoginService2 } from '../login.service';
import { PostLoginActionsService } from '../post-login-actions.service';
import { PostLoginService } from '../post-login.service';
import { RememberMeService } from '../remember-me.service';
import { LoginResponseHandlerContext, LoginResponseHandlerHook } from './login-response-handler-hook';
import { LoginResponseOptions } from './login-response-handler.models';

const REMEMBER_ME_TYPE = 'remember_me';

/**
 * @whatItDoes Provides handling of login response
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class LoginResponseHandlerService {
    private hooks: LoginResponseHandlerHook[] = [];

    private readonly user = inject(UserService);
    private readonly loginStore = inject(LoginStoreService);
    private readonly nativeApplication = inject(NativeAppService);
    private readonly navigationService = inject(NavigationService);
    private readonly loginNavigation = inject(LoginNavigationService);
    private readonly clientConfig = inject(ClientConfigService);
    private readonly rememberMeService = inject(RememberMeService);
    private readonly logger = inject(Logger);
    private readonly postLoginActions = inject(PostLoginActionsService);
    private readonly urlService = inject(UrlService);
    private readonly page = inject(Page);
    private readonly router = inject(Router);
    private readonly cookieService = inject(CookieService);
    private readonly trackingService = inject(TrackingService);
    private readonly loginService = inject(LoginService2);
    private readonly postLoginService = inject(PostLoginService);
    private readonly googleAnalyticsService = inject(GoogleAnalyticsCoreService);
    private readonly datadogService = inject(DatadogRumService);

    /** Register login response hooks. */
    registerHooks(hooks: LoginResponseHandlerHook[]) {
        this.hooks = this.hooks.concat(hooks);
    }

    /** Handles login and returns redirect info */
    async handle(response: LoginResponse, options?: LoginResponseOptions): Promise<LoginRedirectInfo> {
        options = options || {};
        this.loginStore.PostLoginValues = response.postLoginValues || null;

        if (this.nativeApplication.isNative && options.additionalPostLoginCcbParameters) {
            this.cookieService.putObject(CookieName.AdditionalPostLoginOptions, options.additionalPostLoginCcbParameters);
        }

        // phase START
        this.setupClaimsAndUser(response);

        if (!response.isCompleted) {
            this.datadogService.datadogRumSdk?.addAction('loginInterceptor', {
                redirectUrl: response.redirectUrl,
                redirectAction: response.action,
            });

            if (!options.skipInterceptorTracking) {
                const masterAccountID = this.postLoginService.getMasterAccountId();

                const isRememberMe = this.rememberMeService.tokenExists();
                this.googleAnalyticsService.track({
                    name: 'pre_login_interceptor_api',
                    params: {
                        authentification_type: isRememberMe ? REMEMBER_ME_TYPE : this.loginType()?.toLowerCase(),
                        category_event: 'login process',
                        label_event: 'authentication',
                        action_event: 'success',
                        location_event: 'login flow',
                        event_details: 'pre login interceptor',
                    },
                });
                this.trackingService.triggerEvent('Event.Tracking', {
                    'component.CategoryEvent': 'login process',
                    'component.LabelEvent': 'authentication',
                    'component.ActionEvent': 'success',
                    'component.PositionEvent': 'not applicable',
                    'component.LocationEvent': 'login flow',
                    'component.EventDetails': 'pre login interceptor',
                    'component.URLClicked': response.redirectUrl,
                    'user.profile.accountID': response.claims?.[ClaimTypeFullName.AccountId],
                    'user.profile.masterAccountID': masterAccountID,
                    'login.customTimeStamp': this.getCustomTimeStamp(),
                });
            }

            await this.clientConfig.reload([UserConfig]);
            this.applyUserLanguage(options);
            this.sendLoginInterceptorEvent();
            // phase WORKFLOW
            this.logInfo('handle', `Workflow phase`);
            this.user.triggerEvent(new UserPartialLoginEvent());

            const opts = Object.assign(options || {}, { replace: true });
            const redirectInfo = this.createRedirectInfo(response.redirectUrl, response.action, false, opts);
            return Promise.resolve(redirectInfo);
        }

        await this.setupRememberMeToken(response.rememberMeEnabled, options.skipRememberMeSetup);
        this.user.triggerEvent(new UserPreHooksLoginEvent());

        const clientConfigsPromise = this.reloadClientConfigs(false); // run in parallel
        const loginTrackingPromise = this.trackLoginSuccess(response);
        await clientConfigsPromise;
        await loginTrackingPromise;

        this.applyUserLanguage(options);

        if (this.user.workflowType < 0) {
            this.sendLoginInterceptorEvent(true);
        }
        this.logInfo('handle', `Post login phase`);
        let willPossiblyRedirect = true;

        if (this.router.onSameUrlNavigation === 'ignore') {
            const futureRedirect = this.loginNavigation.getStoredLoginRedirect(false);

            /**
             * Remove query string used to force password reminder prompt
             * See {@link LoginDialogService#open}
             */
            futureRedirect.url?.search.delete('q');

            // will possibly redirect if culture is different or url is different
            willPossiblyRedirect = !!options.culture || futureRedirect.url?.absUrl() !== this.navigationService.location.absUrl();
            this.logInfo('handle', `willPossiblyRedirect: ${willPossiblyRedirect}`);
        }

        /// indicates that there are no more redirects/actions and that current handling is the last handling
        const isLastIteration = response.isCompleted && !response.redirectUrl && !response.action;
        const context = new LoginResponseHandlerContext(response, options ?? {}, willPossiblyRedirect, isLastIteration);

        await this.runHooks(context, 'onPostLogin');

        this.logInfo('handle', `Finished post login hooks`);

        if (isLastIteration) {
            this.loginStore.disablePostLoginCcbDelay();
        }

        this.user.triggerEvent(new UserLoginEvent());

        return this.createRedirectInfo(response.redirectUrl, response.action || 'goToRedirectUrl', true, options || {});
    }

    /** Handles login and redirects */
    async handleResponse(response: LoginResponse, options?: LoginResponseOptions) {
        const redirectInfo = await this.handle(response, options);
        redirectInfo.goTo();
    }

    /** Handles update of client configs */
    reloadClientConfigs(reloadClaimsConfig: boolean = true): Promise<any> {
        const configsToReload = reloadClaimsConfig ? [ClaimsConfig] : [];
        return this.clientConfig.reloadOnLogin(configsToReload);
    }

    /** runHooks. */
    runHooks(context: LoginResponseHandlerContext, phase: keyof LoginResponseHandlerHook): Promise<any> {
        return Promise.all(
            this.hooks.filter((hook: LoginResponseHandlerHook) => hook[phase]).map((hook: LoginResponseHandlerHook) => hook[phase]!(context)),
        );
    }

    private createRedirectInfo(
        url: string | ParsedUrl | undefined,
        action: string | undefined,
        isCompleted: boolean,
        options: GoToOptions,
    ): LoginRedirectInfo {
        let parsedUrl: ParsedUrl | undefined;

        if (url && typeof url === 'string') {
            parsedUrl = this.urlService.parse(url);
        }

        let goTo: () => void;

        if (parsedUrl) {
            this.logInfo(
                'createRedirectInfo',
                `goTo_parsedUrl => (parsedUrl: ${JSON.stringify(parsedUrl.absUrl())}, options: ${JSON.stringify(options)})`,
            );
            goTo = () => {
                if (parsedUrl) {
                    this.navigationService.goTo(parsedUrl, options);
                }
            };
        } else if (this.nativeApplication.isNativeApp && isCompleted) {
            this.logInfo('createRedirectInfo', `goTo_goToNativeApp => (options: ${JSON.stringify(options)})`);
            goTo = () => this.navigationService.goToNativeApp(options);
        } else {
            const args = [options];
            this.logInfo('createRedirectInfo', `goTo_postLoginActions => (action: ${action}, args: ${JSON.stringify(args)})`);
            goTo = () => this.postLoginActions.invoke(action, args);
        }

        return { url: parsedUrl, options, isCompleted, goTo };
    }

    private async setupRememberMeToken(rememberMeEnabled?: boolean, skipSetRemembermeSetup?: boolean) {
        if (rememberMeEnabled !== false && !skipSetRemembermeSetup) {
            try {
                await firstValueFrom(this.rememberMeService.setupTokenAfterLogin());
            } catch (error) {
                this.logger.errorRemote('Failed to setup remember-me token. User gets only regular auth session.', error);
            }
        }
    }

    private logInfo(method: string, message: string) {
        const infoMessage: string = `Class: LoginResponseHandlerService. Method: ${method}. Message: ${message}.`;
        this.logger.infoRemote(infoMessage);
    }

    private async trackLoginSuccess(response: LoginResponse) {
        const loginSubmissionType = this.loginService.getLoginSubmissionType(
            this.loginStore.LastVisitor || response.claims?.[ClaimTypeFullName.Username],
        );
        const loginType = this.loginType() || `Login_${loginSubmissionType}`;
        const accountId = response.claims?.[ClaimTypeFullName.AccountId];
        const masterAccountID = this.postLoginService.getMasterAccountId();

        const isRememberMe = this.rememberMeService.tokenExists();
        this.googleAnalyticsService.track({
            name: 'login_success_api',
            params: {
                authentification_type: isRememberMe ? REMEMBER_ME_TYPE : loginType.toLowerCase(),
                authentification_submission_type: isRememberMe ? 'rememberme' : loginSubmissionType?.toLowerCase(),
                category_event: 'login',
                label_event: 'success',
                action_event: 'attempt',
                location_event: this.navigationService.location.absUrl(),
            },
        });
        this.datadogService.datadogRumSdk?.addAction('loginSuccess', {
            username: response.claims?.[ClaimTypeFullName.Username],
            loginType,
        });

        await this.trackingService.triggerEvent('Event.Login', {
            'login.type': loginType,
            'page.loginSubmissionType': loginSubmissionType,
            'page.siteSection': 'Authentication',
            'user.profile.accountID': accountId,
            'user.profile.masterAccountID': masterAccountID,
            'user.profile.bal': response.balance?.accountBalance,
            'user.profile.currency': response.claims?.[ClaimTypeFullName.Currency],
            'user.profile.country': response.claims?.[ClaimTypeFullName.Country],
            'user.profile.loyaltyStatus': response.user?.loyaltyCategory,
            'user.hasPositiveBalance': (response.balance?.accountBalance ?? 0) > 0,
            'user.isAuthenticated': response.user?.isAuthenticated,
            'user.isExisting': !!this.cookieService.get(CookieName.LastVisitor),
            'login.customTimeStamp': this.getCustomTimeStamp(),
        });
    }

    private loginType() {
        return this.cookieService.get(CookieName.LoginType);
    }

    private sendLoginInterceptorEvent(isPostLogin: boolean = false) {
        this.nativeApplication.sendToNative({
            eventName: NativeEventType.LOGIN_INTERCEPTOR,
            parameters: { isPostLogin, accountId: this.user.accountId },
        });
    }

    private setupClaimsAndUser(response: LoginResponse) {
        let configsToUpdate: { [key: string]: any } = {
            vnClaims: response.claims,
        };
        if (response.balance) {
            configsToUpdate['vnBalanceProperties'] = { balanceProperties: response.balance };
        }
        this.clientConfig.update(configsToUpdate, { keepExistingProperties: false });
        this.clientConfig.update(
            {
                vnUser: {
                    isAuthenticated: response.user?.isAuthenticated ?? false,
                    xsrfToken: response.claims?.[ClaimTypeFullName.SessionToken],
                },
            },
            { keepExistingProperties: true },
        );
    }

    private applyUserLanguage(options: LoginResponseOptions) {
        if (this.cookieService.get(CookieName.SkipUserLanguage) !== '1' && this.user.lang !== this.page.lang) {
            this.logInfo('createRedirectInfo', `Change page language to user language`);
            options.culture = this.user.lang;
        }
    }

    private getCustomTimeStamp(): number {
        return Date.now() * 1000;
    }
}
